import React, { createRef, useCallback, useRef } from 'react';

import { useIntl } from 'react-intl';

import type { GasPayload } from '@atlaskit/analytics-gas-types';
import Button from '@atlaskit/button/new';
import { useCloudIdToUrl } from '@atlaskit/linking-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { universalTypeFilterId } from '../../../../common/constants/filters';
import { ProductKeys } from '../../../../common/constants/product-keys';
import { SearchExperience } from '../../../../common/constants/search-experience';
import { AIModeTabs } from '../../../../common/ui/ai-mode';
import { useOverflowObserver } from '../../../../common/ui/overflow-observer';
import { useWidthObserverRegistration } from '../../../../common/ui/width-observer';
import { serveFpsMultiSiteSearchExperiment } from '../../../../common/utils/experiments';
import { FILTER_CHANGE_LOCATION } from '../../../../common/utils/filters/events';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import {
	useCloudId,
	useFeatures,
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
	useUnitMetadata,
} from '../../../../controllers/store/bootstrap';
import {
	useAnyFilterSelected,
	useFiltersToDisplay,
	useSearchActions,
	useSearchQuery,
	useSelectedProduct,
} from '../../../../controllers/store/search';
import { filter as archived } from '../../../../controllers/store/search/filters/archived';
import { filter as archivedAndPendingComponents } from '../../../../controllers/store/search/filters/archived-and-pending-components';
import { filter as assetsSchema } from '../../../../controllers/store/search/filters/assets-schema';
import { filter as assetsType } from '../../../../controllers/store/search/filters/assets-type';
import { filter as assignee } from '../../../../controllers/store/search/filters/assignee';
import { filter as calendarType } from '../../../../controllers/store/search/filters/calendar-type';
import { filter as campaignStatus } from '../../../../controllers/store/search/filters/campaign-status';
import { filter as caseStatus } from '../../../../controllers/store/search/filters/case-status';
import { filter as closeDate } from '../../../../controllers/store/search/filters/close-date';
import { filter as commentedByMe } from '../../../../controllers/store/search/filters/commentedByMe';
import { filter as content } from '../../../../controllers/store/search/filters/content';
import { filter as contributor } from '../../../../controllers/store/search/filters/contributor';
import { filter as createdBy } from '../../../../controllers/store/search/filters/created-by';
import { filter as documentStatus } from '../../../../controllers/store/search/filters/document-status';
import { filter as focusAreaType } from '../../../../controllers/store/search/filters/focus-area-type';
import { filter as focusAreaUnder } from '../../../../controllers/store/search/filters/focus-area-under';
import { filter as hasAttachment } from '../../../../controllers/store/search/filters/has-attachment';
import { filter as label } from '../../../../controllers/store/search/filters/label';
import { filter as lastUpdated } from '../../../../controllers/store/search/filters/last-updated';
import { filter as lastUpdatedBy } from '../../../../controllers/store/search/filters/last-updated-by';
import { filter as leadStatus } from '../../../../controllers/store/search/filters/lead-status';
import { filter as opportunityStatus } from '../../../../controllers/store/search/filters/opportunity-status';
import { filter as owner } from '../../../../controllers/store/search/filters/owner';
import { filter as pageStatus } from '../../../../controllers/store/search/filters/page-status';
import { filter as positionFocusArea } from '../../../../controllers/store/search/filters/position-focus-area';
import { filter as positionJobFamily } from '../../../../controllers/store/search/filters/position-job-family';
import { filter as positionStatus } from '../../../../controllers/store/search/filters/position-status';
import { filter as positionTeam } from '../../../../controllers/store/search/filters/position-team';
import { filter as project } from '../../../../controllers/store/search/filters/project';
import { filter as pullRequestStatus } from '../../../../controllers/store/search/filters/pull-request-status';
import { filter as reporter } from '../../../../controllers/store/search/filters/reporter';
import { filter as repository } from '../../../../controllers/store/search/filters/repository';
import { filter as reviewer } from '../../../../controllers/store/search/filters/reviewer';
import { filter as salesforceStage } from '../../../../controllers/store/search/filters/salesforce-stage';
import { filter as slackChannel } from '../../../../controllers/store/search/filters/slack-channel';
import { filter as sort } from '../../../../controllers/store/search/filters/sort';
import { filter as space } from '../../../../controllers/store/search/filters/space';
import { filter as status } from '../../../../controllers/store/search/filters/status';
import { filter as taskStatus } from '../../../../controllers/store/search/filters/task-status';
import { filter as taskType } from '../../../../controllers/store/search/filters/task-type';
import { filter as titleMatchOnly } from '../../../../controllers/store/search/filters/title-match-only';
import { filter as verified } from '../../../../controllers/store/search/filters/verified';
import { filter as watched } from '../../../../controllers/store/search/filters/watched';
import { filter as workspaceName } from '../../../../controllers/store/search/filters/workspace-name';
import {
	type OnFilterChangeCallback,
	useFilterAnalytics,
	// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
} from '../../../quick-find/dialog/filters/inline-product-filters/utils';

import AppSelectFilter from './app-select';
import ArchivedFilter from './archived';
import ArchivedAndPendingComponentsFilter from './archived-and-pending-components';
import AssetsSchemaFilter from './assets-schema';
import AssetsTypeFilter from './assets-type';
import AssigneeFilter from './assignee';
import CalendarTypeFilter from './calendar-type';
import CampaignStatusFilter from './campaign-status';
import CaseStatusFilter from './case-status';
import CloseDateFilter from './close-date';
import CommentedByMeFilter from './commented-by-me';
import ContentFilter from './content';
import ContributorFilter from './contributor';
import CreatedByFilter from './created-by';
import DocumentStatusFilter from './document-status';
import DropdownFilter from './dropdown';
import FocusAreaTypeFilter from './focus-area-type';
import FocusAreaUnderFilter from './focus-area-under';
import HasAttachmentFilter from './has-attachment';
import LabelFilter from './label';
import LastUpdatedFilter from './last-updated';
import LastUpdatedByFilter from './last-updated-by';
import LeadStatusFilter from './lead-status';
import { i18n } from './messages';
import OpportunityStatusFilter from './opportunity-status';
import OwnerFilter from './owner';
import PageStatusFilter from './page-status';
import PositionFocusAreaFilter from './position-focus-area';
import PositionJobFamilyFilter from './position-job-family';
import PositionStatusFilter from './position-status';
import PositionTeamFilter from './position-team';
import ProjectFilter from './project';
import PullRequestStatusFilter from './pull-request-status';
import ReporterFilter from './reporter';
import RepositoryFilter from './repository';
import { ResultsSort } from './results-sort';
import ReviewerFilter from './reviewer';
import SalesforceStageFilter from './salesforce-stage';
import SiteSelectFilter from './site-select/site-select-filter';
import SlackChannelFilter from './slack-channel';
import SpaceFilter from './space';
import StatusFilter from './status';
import TaskStatusFilter from './task-status';
import TaskTypeFilter from './task-type';
import TitleMatchOnlyFilter from './title-match-only';
import UniversalTypeFilter from './universal-type';
import VerifiedFilter from './verified';
import WatchedFilter from './watched';
import WorkspaceNameFilter from './workspace-name';

export const filters: {
	archived: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		onFilterChange,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	lastUpdated: ({
		testId,
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
	}: {
		testId?: string;
	} & import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	universalTypeFilter: ({
		testId,
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: {
		testId?: string;
	} & import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	contributor: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	space: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	focusAreaType: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	focusAreaUnder: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	verified: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		onFilterChange,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	watchedByMe: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		onFilterChange,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	assignee: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	reporter: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	commentedByMe: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		onFilterChange,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	project: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	content: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	onlyTitleMatch: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		onFilterChange,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	owner: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	createdBy: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	lastUpdatedBy: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	label: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	pageStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	statusFilter: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	compassArchived: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		onFilterChange,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	assetsSchema: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	assetsType: ({
		isFilterInDropdown,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => JSX.Element | null;
	positionStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	positionFocusArea: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	positionJobFamily: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	positionTeam: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	salesforceStage: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	closeDate: ({
		testId,
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
	}: {
		testId?: string;
	} & import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	campaignStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	caseStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	documentStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	calendarType: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	leadStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	taskStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	taskType: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	opportunityStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	pullRequestStatus: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	repository: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	reviewer: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	slackChannel: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	workspaceName: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		fit,
		onFilterChange,
		hideOnHoverOut,
		onClearOptions,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
	hasAttachment: ({
		isFilterInDropdown,
		showPrimaryProductIcon,
		onFilterChange,
	}: import('../../../../common/ui/filters/product-filters/types').FilterProps) => React.JSX.Element | null;
} = {
	[archived.id]: ArchivedFilter,
	[lastUpdated.id]: LastUpdatedFilter,
	[universalTypeFilterId]: UniversalTypeFilter,
	[contributor.id]: ContributorFilter,
	[space.id]: SpaceFilter,
	[focusAreaType.id]: FocusAreaTypeFilter,
	[focusAreaUnder.id]: FocusAreaUnderFilter,
	[verified.id]: VerifiedFilter,
	[watched.id]: WatchedFilter,
	[assignee.id]: AssigneeFilter,
	[reporter.id]: ReporterFilter,
	[commentedByMe.id]: CommentedByMeFilter,
	[project.id]: ProjectFilter,
	[content.id]: ContentFilter,
	[titleMatchOnly.id]: TitleMatchOnlyFilter,
	[owner.id]: OwnerFilter,
	[createdBy.id]: CreatedByFilter,
	[lastUpdatedBy.id]: LastUpdatedByFilter,
	[label.id]: LabelFilter,
	[pageStatus.id]: PageStatusFilter,
	[status.id]: StatusFilter,
	[archivedAndPendingComponents.id]: ArchivedAndPendingComponentsFilter,
	[assetsSchema.id]: AssetsSchemaFilter,
	[assetsType.id]: AssetsTypeFilter,
	[positionStatus.id]: PositionStatusFilter,
	[positionFocusArea.id]: PositionFocusAreaFilter,
	[positionJobFamily.id]: PositionJobFamilyFilter,
	[positionTeam.id]: PositionTeamFilter,
	[salesforceStage.id]: SalesforceStageFilter,
	[closeDate.id]: CloseDateFilter,
	[campaignStatus.id]: CampaignStatusFilter,
	[caseStatus.id]: CaseStatusFilter,
	[documentStatus.id]: DocumentStatusFilter,
	[calendarType.id]: CalendarTypeFilter,
	[leadStatus.id]: LeadStatusFilter,
	[taskStatus.id]: TaskStatusFilter,
	[taskType.id]: TaskTypeFilter,
	[opportunityStatus.id]: OpportunityStatusFilter,
	[pullRequestStatus.id]: PullRequestStatusFilter,
	[repository.id]: RepositoryFilter,
	[reviewer.id]: ReviewerFilter,
	[slackChannel.id]: SlackChannelFilter,
	[workspaceName.id]: WorkspaceNameFilter,
	[hasAttachment.id]: HasAttachmentFilter,
};

const inlineStyles = xcss({
	overflow: 'hidden',
	padding: 'space.050',
	margin: 'space.negative.050',
	flex: '1 0 auto',
	position: 'absolute',
	zIndex: 'card',
});

const aiTabsStyles = xcss({
	position: 'absolute',
	zIndex: 'card',
	padding: 'space.050',
	margin: 'space.negative.050',
});

const inlineWithAIModeTabsStyles = xcss({
	overflow: 'hidden',
	padding: 'space.050',
	margin: 'space.negative.050',
	flex: '1 0 auto',
	position: 'absolute',
	zIndex: 'card',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	marginInlineStart: '190px' as any,
});

const hiddenReferenceInlineStyles = xcss({
	overflow: 'hidden',
	padding: 'space.050',
	margin: 'space.negative.050',
	flex: '1 0 auto',
	visibility: 'hidden',
	position: 'relative',
	zIndex: '1',
});

const filterRowStyles = xcss({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
});

const suggestedFiltersDividerStyles = xcss({
	borderLeft: `${token('border.width')} solid ${token('color.border')}`,
	height: '29px',
	marginInline: 'space.150',
	width: '1px',
});

const appSelectorFiltersDividerStyles = xcss({
	borderLeft: `${token('border.width')} solid ${token('color.border')}`,
	height: '29px',
	width: '1px',
	alignSelf: 'center',
});

const suggestedFilterStyles = xcss({
	alignItems: 'center',
	color: 'color.text.subtlest',
	display: 'flex',
	gap: 'space.100',
	width: 'max-content',
});

interface ClearAllButtonProps {
	/** Called when the user wants to clear all filters. */
	onClear: VoidFunction;
}

export const ClearAllButton: ({ onClear }: ClearAllButtonProps) => React.JSX.Element = ({
	onClear,
}: ClearAllButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box id="clear-all-button">
			<Button onClick={onClear} appearance="subtle">
				<Text>{formatMessage(i18n.clearFilters)}</Text>
			</Button>
		</Box>
	);
};

const SuggestedFilters = ({
	primaryProduct,
	onFilterChange,
	onClearOptions,
}: {
	primaryProduct: ProductKeys;
	onFilterChange?: OnFilterChangeCallback;
	onClearOptions?: (params: { filterId: string }) => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={suggestedFilterStyles}>
			<Box xcss={suggestedFiltersDividerStyles}></Box>
			{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
			<Text as="p">{`${formatMessage(i18n.suggestedFilters)}:`}</Text>
			{primaryProduct === ProductKeys.Confluence && (
				<>
					<SpaceFilter
						showPrimaryProductIcon
						onFilterChange={(change) =>
							onFilterChange?.(space.id, change, FILTER_CHANGE_LOCATION.INLINE_FPS_SUGGESTION)
						}
						onClearOptions={onClearOptions}
					/>
					{fg('rovo_fps_sort_in_suggested_filters') && (
						<ResultsSort
							onFilterChange={(change) =>
								onFilterChange?.(sort.id, change, FILTER_CHANGE_LOCATION.INLINE_FPS_SUGGESTION)
							}
							onClearOptions={onClearOptions}
							showPrimaryProductIcon
						/>
					)}
				</>
			)}
			{primaryProduct === ProductKeys.Assets && fg('assets_rovo_search_all') && (
				<AssetsSchemaFilter
					showPrimaryProductIcon
					onFilterChange={(change) =>
						onFilterChange?.(assetsSchema.id, change, FILTER_CHANGE_LOCATION.INLINE_FPS_SUGGESTION)
					}
					onClearOptions={onClearOptions}
				/>
			)}
			{primaryProduct === ProductKeys.Jira && (
				<>
					<ProjectFilter
						showPrimaryProductIcon
						onFilterChange={(change) =>
							onFilterChange?.(project.id, change, FILTER_CHANGE_LOCATION.INLINE_FPS_SUGGESTION)
						}
						onClearOptions={onClearOptions}
					/>
					{fg('rovo_fps_sort_in_suggested_filters') && (
						<ResultsSort
							onFilterChange={(change) =>
								onFilterChange?.(sort.id, change, FILTER_CHANGE_LOCATION.INLINE_FPS_SUGGESTION)
							}
							onClearOptions={onClearOptions}
							showPrimaryProductIcon
						/>
					)}
				</>
			)}
		</Box>
	);
};

type ProductFiltersProps = {
	containerRef: React.MutableRefObject<HTMLDivElement | undefined>;
};

const ProductFilters = ({ containerRef }: ProductFiltersProps): React.JSX.Element => {
	const [cloudId] = useCloudId();
	const [query] = useSearchQuery();
	const { data: tenantDomain } = useCloudIdToUrl(cloudId || '');
	const [{ filtersInLine }] = useFiltersToDisplay();
	const [productSelected] = useSelectedProduct();
	const { resetSort, clearAllFilters } = useSearchActions();
	const [isAnyFilterSelected] = useAnyFilterSelected();
	const isConfluence = productSelected === ProductKeys.Confluence;
	const isJira = productSelected === ProductKeys.Jira;
	const isCompass = productSelected === ProductKeys.Compass;
	const [primaryProduct] = usePrimaryProduct();
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [features] = useFeatures();
	const isLiteMode = features?.lite?.enabled ?? false;
	const unitMetadata = useUnitMetadata();
	const showSiteSelector =
		filtersDiscoverability2Enabled &&
		!isLiteMode &&
		// Only surface the selector when the tenant has more than one site to choose from (QS-9672).
		(unitMetadata?.length ?? 0) > 1 &&
		serveFpsMultiSiteSearchExperiment({ primaryProduct });
	// Elements in the navbar that will never be hidden despite resizes
	const alwaysVisibleRef = useRef<HTMLDivElement>();
	const dropdownRef = useRef<HTMLDivElement>();
	const appSelectFilterRef = useRef<HTMLDivElement>();
	const siteSelectFilterRef = useRef<HTMLDivElement>();

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();

	const fireAnalyticsCb = useCallback(
		(payload: GasPayload) => {
			const { attributes, ...rest } = payload;
			fireAnalyticsEvent({
				...rest,
				attributes: {
					// Search-page needs to have attributes within additionalAttributes or else it gets ommitted when
					// the event is emitted
					additionalAttributes: {
						...attributes,
					},
				},
			});
		},
		[fireAnalyticsEvent],
	);
	const { onFilterChange, onDropdownOpenChange, onFilterClearAction, onSingleFilterClearAction } =
		useFilterAnalytics(fireAnalyticsCb, commonAttributes, SearchExperience.SEARCH_PAGE);

	let filtersRendered = filtersInLine.filter(
		(id) => id !== universalTypeFilterId || productSelected !== ProductKeys.Atlas,
	);

	// Hide content under filter from navbar QS-5384
	if (isConfluence) {
		filtersRendered = filtersRendered.filter((id) => id !== content.id);
	} else if (isCompass) {
		filtersRendered = filtersRendered.filter((id) => id !== archivedAndPendingComponents.id);
	} else if (productSelected === ProductKeys.Assets && fg('assets_rovo_search_all')) {
		filtersRendered = filtersRendered.filter((id) => id !== contributor.id);
	} else if (productSelected === ProductKeys.Salesforce) {
		filtersRendered = filtersRendered.filter((id) => id !== owner.id);
	}

	// Owner, Stage, and Close Date filters for Salesforce should only appear in the dropdown panel.
	if (productSelected === ProductKeys.Salesforce) {
		filtersRendered = filtersRendered.filter(
			(id) => id !== owner.id && id !== salesforceStage.id && id !== closeDate.id,
		);
	}

	if (
		// don't show assets filters on townsquare if the flag is set
		// eslint-disable-next-line @atlaskit/platform/no-preconditioning
		primaryProduct === ProductKeys.Atlas &&
		fg('assets_rovo_search_all') &&
		!fg('rovo_search_show_assets_filters_in_townsquare')
	) {
		filtersRendered = filtersRendered.filter(
			(id) => id !== assetsSchema.id && id !== assetsType.id,
		);
	}

	// Note: filter visibility is centralised in `useFiltersToDisplay` which already applies
	// FILTER_EXCLUSION_RULES + FILTER_INCLUSION_RULES via `getExcludedFilterIds`. The list
	// returned here (`filtersRendered`) is therefore already projected and we don't need to
	// re-apply the exclusion at the call site.

	const filterRefs = useRef<React.MutableRefObject<HTMLDivElement>[]>([]);

	filterRefs.current = filtersRendered
		.filter((id) => filters[id as keyof typeof filters])
		.map((_, i) => filterRefs.current[i] ?? createRef());

	const visibleFilters = useOverflowObserver({
		containerRef,
		alwaysVisibleRef,
		itemRefs: filterRefs,
		itemIds: filtersRendered,
	});

	const filtersRef = useRef(null);
	useWidthObserverRegistration('filters', filtersRef);

	const displaySuggestedConfluenceFilters =
		primaryProduct === ProductKeys.Confluence && !isAnyFilterSelected;
	const displaySuggestedAssetsFilters =
		primaryProduct === ProductKeys.Assets && !isAnyFilterSelected && fg('assets_rovo_search_all');
	const displaySuggestedJiraFilters = primaryProduct === ProductKeys.Jira && !isAnyFilterSelected;
	const displaySuggestedFilters =
		displaySuggestedConfluenceFilters ||
		displaySuggestedAssetsFilters ||
		displaySuggestedJiraFilters;

	return (
		<Box xcss={filterRowStyles}>
			<Inline space="space.100" xcss={hiddenReferenceInlineStyles}>
				{showSiteSelector && (
					<>
						<Box
							style={{
								// Fallback before the ref is measured: the site name can be long, so reserve the
								// FilterButton max width (280px) rather than under-reserving and shifting layout.
								width: siteSelectFilterRef.current?.offsetWidth ?? 280,
							}}
						/>
						<Box xcss={appSelectorFiltersDividerStyles}></Box>
					</>
				)}
				<Box
					style={{
						width: dropdownRef.current?.offsetWidth ?? 40,
					}}
				/>
				{filtersDiscoverability2Enabled && !isLiteMode && (
					<Box
						style={{
							width: appSelectFilterRef.current?.offsetWidth ?? 96,
						}}
					/>
				)}
				{filtersRendered.map((id, index) => {
					const Filter = filters[id as keyof typeof filters];
					if (!Filter) {
						return null;
					}
					const elementId = `reference:${id}`;
					return (
						<React.Fragment key={elementId}>
							{index === 0 && !isLiteMode && <Box xcss={appSelectorFiltersDividerStyles}></Box>}
							<Box id={elementId} ref={filterRefs.current[index]}>
								{id === lastUpdated.id || id === universalTypeFilterId ? (
									<Filter
										testId={`ref-test-id-${id}`}
										onFilterChange={(change) =>
											onFilterChange(id, change, FILTER_CHANGE_LOCATION.INLINE_FPS)
										}
										onClearOptions={onSingleFilterClearAction}
									/>
								) : (
									<Filter
										onFilterChange={(change) =>
											onFilterChange(id, change, FILTER_CHANGE_LOCATION.INLINE_FPS)
										}
										onClearOptions={onSingleFilterClearAction}
									/>
								)}
							</Box>
						</React.Fragment>
					);
				})}
			</Inline>
			{fg('rovo_ai_mode_restore_toggle_search') && (
				<Box xcss={aiTabsStyles}>
					<AIModeTabs query={query} tenantDomain={tenantDomain} />
				</Box>
			)}
			<Inline
				space="space.100"
				xcss={fg('rovo_ai_mode_restore_toggle_search') ? inlineWithAIModeTabsStyles : inlineStyles}
				ref={filtersRef}
				role="list"
			>
				{showSiteSelector && (
					<>
						<Inline ref={siteSelectFilterRef} role="listitem">
							<SiteSelectFilter />
						</Inline>
						<Box xcss={appSelectorFiltersDividerStyles}></Box>
					</>
				)}
				<Inline ref={dropdownRef} role="listitem">
					<DropdownFilter
						onFiltersChange={onFilterChange}
						onDropdownOpenChange={onDropdownOpenChange}
						onClearOptions={onSingleFilterClearAction}
					/>
				</Inline>
				{filtersDiscoverability2Enabled && !isLiteMode && (
					<Inline ref={appSelectFilterRef} role="listitem">
						<AppSelectFilter />
					</Inline>
				)}
				{filtersRendered.map((id, index) => {
					const Filter = filters[id as keyof typeof filters];
					if (!visibleFilters.includes(id) || !Filter) {
						return null;
					}
					return (
						<React.Fragment key={id}>
							{index === 0 && !isLiteMode && <Box xcss={appSelectorFiltersDividerStyles}></Box>}
							<Box id={id} role="listitem">
								<Filter
									onFilterChange={(change) =>
										onFilterChange(id, change, FILTER_CHANGE_LOCATION.INLINE_FPS)
									}
									onClearOptions={onSingleFilterClearAction}
								/>
							</Box>
						</React.Fragment>
					);
				})}
				<Inline space="space.100" ref={alwaysVisibleRef} role="listitem">
					{(isConfluence || isJira) && <ResultsSort />}
					{isAnyFilterSelected ? (
						<ClearAllButton
							onClear={() => {
								clearAllFilters();
								resetSort();
								onFilterClearAction('inline-search-page');
							}}
						/>
					) : null}
					{displaySuggestedFilters && (
						<SuggestedFilters
							primaryProduct={primaryProduct}
							onFilterChange={onFilterChange}
							onClearOptions={onSingleFilterClearAction}
						/>
					)}
				</Inline>
			</Inline>
		</Box>
	);
};

export default ProductFilters;
